import { useState } from "react";
import { Box, ChevronDown, ChevronRight, Table2 } from "lucide-react";
import { CURVE_FORMULA_FIELDS } from "@brewbase/lib";
import { cn } from "@/lib/utils";
import { CURVE_FIELD_LABELS } from "../curve-fields";
import type {
  FormulaContextSpec,
  IdentifierRef,
  SampleTestRef,
} from "../context";

interface ReferenceTreeProps {
  context: FormulaContextSpec;
  onInsert: (snippet: string) => void;
}

export function ReferenceTree({ context, onInsert }: ReferenceTreeProps) {
  return (
    <div className="flex flex-col divide-y divide-olive-4">
      {context.scopes.local.length > 0 && (
        <Group label="Local">
          {context.scopes.local.map((r) =>
            r.kind === "TABULAR_DATA" ? (
              <TabularBranch
                key={r.code}
                table={r}
                functions={context.functions}
                onInsert={onInsert}
              />
            ) : r.kind === "INVENTORY_ITEM_REFERENCE" &&
              r.referenceProperties?.length ? (
              <InventoryRefBranch
                key={r.code}
                reference={r}
                onInsert={onInsert}
              />
            ) : (
              <Leaf
                key={r.code}
                label={r.code}
                meta={[r.unit, r.kind].filter(Boolean).join(" · ")}
                description={r.sourceDescription}
                onClick={() => onInsert(r.code)}
              />
            ),
          )}
        </Group>
      )}
      {context.scopes.upstream && context.scopes.upstream.length > 0 && (
        <Group label="Upstream">
          {context.scopes.upstream.map((r) => (
            <Leaf
              key={r.code}
              label={`upstream.${r.code}`}
              description={r.sourceDescription}
              onClick={() => onInsert(`upstream.${r.code}`)}
            />
          ))}
        </Group>
      )}
      {context.scopes.samples && context.scopes.samples.length > 0 && (
        <Group label="Samples">
          {context.scopes.samples.map((sample) => (
            <SampleBranch
              key={sample.abbreviation}
              abbr={sample.abbreviation}
              name={sample.name}
              tests={sample.tests}
              onInsert={onInsert}
            />
          ))}
        </Group>
      )}
      {context.scopes.curves && context.scopes.curves.length > 0 && (
        <Group label="Calibration curves">
          {context.scopes.curves.map((curve) => (
            <CurveBranch key={curve.code} curve={curve} onInsert={onInsert} />
          ))}
        </Group>
      )}
      {context.scopes.tabular &&
        (context.scopes.tabular.columns.length > 0 ||
          context.scopes.tabular.index) && (
          <Group label="Row">
            {context.scopes.tabular.columns.map((c) => (
              <Leaf
                key={c.code}
                label={`row.${c.code}`}
                meta={[c.unit, c.kind].filter(Boolean).join(" · ")}
                onClick={() => onInsert(`row.${c.code}`)}
              />
            ))}
            {context.scopes.tabular.index && (
              <Leaf
                label="elapsed"
                meta="time since previous row"
                description={`Duration from the previous row's ${context.scopes.tabular.index.name} to this one's. Blank on the first row.`}
                onClick={() => onInsert("elapsed")}
              />
            )}
          </Group>
        )}
    </div>
  );
}

function Group({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="px-2 py-1 text-xs uppercase tracking-wide text-olive-10 bg-olive-3">
        {label}
      </div>
      <div className="flex flex-col">{children}</div>
    </div>
  );
}

function Leaf({
  label,
  meta,
  description,
  onClick,
}: {
  label: string;
  meta?: string;
  description?: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="w-full text-left px-3 py-1 hover:bg-olive-3 flex items-center justify-between gap-2 group"
      onClick={onClick}
      title={description}
    >
      {/* Code never wraps — ellipsis if it must; the short meta keeps its width. */}
      <span className="font-mono text-sm text-olive-12 truncate min-w-0">
        {label}
      </span>
      {meta && (
        <span className="text-xs text-olive-10 shrink-0">{meta}</span>
      )}
    </button>
  );
}

function SubLabel({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "px-3 pt-1.5 pb-0.5 text-xs font-semibold text-olive-9",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A tabular-data local: expandable to reveal its columns and the two ways to
 * reference them — pick a row then read a column (member access, the canonical
 * form), or use a whole column (projection, for aggregates).
 */
function TabularBranch({
  table,
  functions,
  onInsert,
}: {
  table: IdentifierRef;
  functions: FormulaContextSpec["functions"];
  onInsert: (snippet: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const code = table.code;
  const columns = table.tabularColumns ?? [];

  // Row selectors, generated from every function that takes this table and
  // returns a row/table — so the full family (first/last/nth/get/find/findlast/
  // maxrow/minrow/medianrow/where/resample) stays in sync. A trailing `.` or a
  // `$0` caret makes the editor pop column/condition completions.
  const selectors = functions
    .filter(
      (fn) =>
        (fn.returnType === "object" || fn.returnType === "table") &&
        fn.parameters[0]?.type === "table",
    )
    .map((fn) => {
      const oneArg = fn.parameters.length <= 1;
      return {
        label: oneArg ? `${fn.name}(${code}).` : `${fn.name}(${code}, …)`,
        meta: fn.description,
        snippet: oneArg ? `${fn.name}(${code}).` : `${fn.name}(${code}, $0)`,
      };
    });

  return (
    <div>
      <button
        type="button"
        className="w-full text-left px-3 py-1 hover:bg-olive-3 flex items-center gap-1"
        onClick={() => setOpen((x) => !x)}
        title={table.sourceDescription}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Table2 size={12} className="text-olive-9" />
        <span className="font-mono text-sm text-olive-12">{code}</span>
        <span className="text-xs text-olive-10 truncate">table</span>
      </button>
      {open && (
        <div className="pl-5">
          {columns.length > 0 && (
            <>
              <SubLabel>Select / filter rows, then read a column</SubLabel>
              {selectors.map((s) => (
                <button
                  key={s.label}
                  type="button"
                  className="w-full text-left px-3 py-1 hover:bg-olive-3 flex flex-col gap-0.5"
                  onClick={() => onInsert(s.snippet)}
                >
                  {/* Code stays on one line; the description wraps below it. */}
                  <span className="font-mono text-sm text-olive-12 truncate">
                    {s.label}
                  </span>
                  <span className="text-xs text-olive-10">{s.meta}</span>
                </button>
              ))}
              <SubLabel className="pt-4">Whole column (for avg, sum, …)</SubLabel>
              {columns.map((c) => (
                <Leaf
                  key={c.code}
                  label={`${code}.${c.code}`}
                  meta={[c.unit, c.kind].filter(Boolean).join(" · ")}
                  onClick={() => onInsert(`${code}.${c.code}`)}
                />
              ))}
            </>
          )}
          {columns.length === 0 && (
            <SubLabel>No columns defined yet.</SubLabel>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * An inventory-reference local: expandable to reveal the resource properties it
 * exposes (e.g. `density`), each inserted as `ref.property`. The header only
 * toggles — a bare reference is a row, not a number, so it's never inserted on
 * its own.
 */
function InventoryRefBranch({
  reference,
  onInsert,
}: {
  reference: IdentifierRef;
  onInsert: (snippet: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const code = reference.code;
  const props = reference.referenceProperties ?? [];

  return (
    <div>
      <button
        type="button"
        className="w-full text-left px-3 py-1 hover:bg-olive-3 flex items-center gap-1"
        onClick={() => setOpen((x) => !x)}
        title={reference.sourceDescription}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <Box size={12} className="text-olive-9" />
        <span className="font-mono text-sm text-olive-12">{code}</span>
        <span className="text-xs text-olive-10 truncate">reference</span>
      </button>
      {open && (
        <div className="pl-5">
          <SubLabel>Resource properties</SubLabel>
          {props.map((p) => (
            <Leaf
              key={p.code}
              label={`${code}.${p.code}`}
              meta={[p.unit, p.kind].filter(Boolean).join(" · ")}
              description={p.name}
              onClick={() => onInsert(`${code}.${p.code}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * A calibration curve of the template: expandable to its numbers, each
 * inserted as `curves.<compound>.<field>`. The header only toggles, since a
 * curve on its own is not a number.
 */
function CurveBranch({
  curve,
  onInsert,
}: {
  curve: IdentifierRef;
  onInsert: (snippet: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        className="w-full text-left px-3 py-1 hover:bg-olive-3 flex items-center gap-1"
        onClick={() => setOpen((x) => !x)}
        title={curve.sourceDescription}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="font-mono text-sm text-olive-12">{curve.code}</span>
        <span className="text-xs text-olive-10 truncate">{curve.name}</span>
      </button>
      {open && (
        <div className="pl-5">
          {CURVE_FORMULA_FIELDS.map((field) => (
            <Leaf
              key={field}
              label={`curves.${curve.code}.${field}`}
              meta={CURVE_FIELD_LABELS[field]}
              description={curve.sourceDescription}
              onClick={() => onInsert(`curves.${curve.code}.${field}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function SampleBranch({
  abbr,
  name,
  tests,
  onInsert,
}: {
  abbr: string;
  name: string;
  tests: SampleTestRef[];
  onInsert: (snippet: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        className="w-full text-left px-3 py-1 hover:bg-olive-3 flex items-center gap-1"
        onClick={() => setOpen((x) => !x)}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="font-mono text-sm">{abbr}</span>
        <span className="text-xs text-olive-10 truncate">{name}</span>
      </button>
      {open && (
        <div className="pl-5">
          {tests.map((t) => (
            <TestBranch
              key={t.code}
              abbr={abbr}
              testCode={t.code}
              testName={t.name}
              attrs={t.attrs}
              onInsert={onInsert}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function TestBranch({
  abbr,
  testCode,
  testName,
  attrs,
  onInsert,
}: {
  abbr: string;
  testCode: string;
  testName: string;
  attrs: { code: string; name: string; unit?: string; kind?: string }[];
  onInsert: (snippet: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        type="button"
        className="w-full text-left px-3 py-1 hover:bg-olive-3 flex items-center gap-1"
        onClick={() => setOpen((x) => !x)}
      >
        {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        <span className="font-mono text-sm">{testCode}</span>
        <span className="text-xs text-olive-10 truncate">{testName}</span>
      </button>
      {open && (
        <div className="pl-5">
          {attrs.map((a) => (
            <Leaf
              key={a.code}
              label={a.code}
              meta={[a.unit, a.kind].filter(Boolean).join(" · ")}
              onClick={() =>
                onInsert(`samples.${abbr}.${a.code}`)
              }
            />
          ))}
        </div>
      )}
    </div>
  );
}
